import { Tooltip } from "primereact/tooltip";
import { classNames } from "primereact/utils";

import { Checkbox } from "../checkbox";
import { Dialog } from "../dialog";
import { DialogFooter } from "../dialog-footer";
import { FormField } from "../form-field";
import { RadioGroup } from "../radio-group";
import { SelectableCard } from "../selectable-card";
import {
  TABLE_SETTINGS_APPLY_LABEL,
  TABLE_SETTINGS_COLUMNS_TITLE,
  TABLE_SETTINGS_DENSITY_TITLE,
} from "./messages";
import styles from "./table-settings-dialog.module.scss";
import type { TableDensity, TableSettingsDialogViewProps } from "./types-table-settings-dialog";

export function TableSettingsDialogView<C extends string>({
  rest,
  className,
  style,
  classNames: parts,
  title,
  isOpen,
  cancel,
  apply,
  testId,
  dialogTestId,
  footerTestId,
  applyTestId,
  densityTestId,
  columnsTestId,
  densityTitleId,
  columnsTitleId,
  density,
  onDensityChange,
  densityOptions,
  showColumnsSection,
  minColumnsTooltipTarget,
  lockedColumnsTooltip,
  columns,
}: TableSettingsDialogViewProps<C>) {
  return (
    <Dialog
      visible={isOpen}
      onHide={cancel}
      size="sm"
      data-testid={dialogTestId}
      className={classNames(styles.root, className)}
      style={style}
      header={<span className={parts?.header}>{title}</span>}
      contentClassName={parts?.body}
      footer={
        <DialogFooter
          data-testid={footerTestId}
          testId={testId}
          confirmTestId={applyTestId}
          confirmLabel={TABLE_SETTINGS_APPLY_LABEL}
          onCancel={cancel}
          onConfirm={apply}
          className={parts?.footer}
          cancelClassName={parts?.cancel}
          confirmClassName={parts?.confirm}
        />
      }
      {...rest}
    >
      <div className={styles.body} data-testid={testId}>
        <section className={classNames(styles.section, parts?.section)}>
          <h3 id={densityTitleId} className={classNames(styles.sectionTitle, parts?.sectionTitle)}>
            {TABLE_SETTINGS_DENSITY_TITLE}
          </h3>
          <RadioGroup.Root<TableDensity>
            aria-labelledby={densityTitleId}
            value={density}
            onChange={onDensityChange}
            testId={densityTestId}
          >
            {densityOptions.map((option) => (
              <SelectableCard
                key={option.value}
                id={option.id}
                value={option.value}
                data-testid={option.testId}
                aria-labelledby={option.titleId}
                aria-describedby={option.descriptionId}
                className={styles.card}
              >
                <span className={styles.cardCopy}>
                  <span id={option.titleId} className={styles.cardTitle}>
                    {option.label}
                  </span>
                  <span id={option.descriptionId} className={styles.cardDescription}>
                    {option.description}
                  </span>
                </span>
              </SelectableCard>
            ))}
          </RadioGroup.Root>
        </section>
        {showColumnsSection ? (
          <section className={classNames(styles.section, parts?.section)}>
            <h3
              id={columnsTitleId}
              className={classNames(styles.sectionTitle, parts?.sectionTitle)}
            >
              {TABLE_SETTINGS_COLUMNS_TITLE}
            </h3>
            <div
              role="group"
              aria-labelledby={columnsTitleId}
              className={styles.columns}
              data-testid={columnsTestId}
            >
              {columns.map((item) => (
                <FormField.Root
                  key={item.column}
                  id={item.id}
                  labelPosition="right"
                  className={classNames(styles.column, item.targetClassName)}
                >
                  <FormField.Label>{item.label}</FormField.Label>
                  <FormField.Control>
                    {/* Coluna principal: marcada e travada, sem esmaecer (decisão do usuário): `readOnly` impede o clique e
                        `aria-disabled` avisa o leitor de tela, sem o estilo de desabilitado. */}
                    <Checkbox
                      checked={item.checked}
                      onChange={item.onChange}
                      readOnly={item.locked}
                      disabled={item.disabled || undefined}
                      aria-disabled={item.locked || undefined}
                    />
                  </FormField.Control>
                </FormField.Root>
              ))}
            </div>
            <Tooltip
              target={minColumnsTooltipTarget}
              content={lockedColumnsTooltip}
              position="top"
              event="both"
            />
          </section>
        ) : null}
      </div>
    </Dialog>
  );
}
